import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { useRegister } from '../api';
import { useToast } from '@/hooks/useToast';
import { AuthShell, FloatField, PwStrengthMeter } from '../AuthShared';
import { SsoButtons } from '../components/SsoButtons';

// ── Validation schemas ────────────────────────────────────────────────────────

const fullNameSchema = z.string().trim().min(1, 'Full name is required');
const emailFieldSchema = z.string().min(1, 'Email is required').email('Invalid email address');
const passwordRegisterSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Must contain an uppercase letter')
  .regex(/[a-z]/, 'Must contain a lowercase letter')
  .regex(/\d/, 'Must contain a number');
const confirmPasswordSchema = z.string().min(8, 'Confirm password must be at least 8 characters');

// SVG icons
// ── Component ─────────────────────────────────────────────────────────────────

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const registerMutation = useRegister();
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const state = location.state as { message?: string };
    if (state?.message) toast.warning(state.message);
  }, [location, toast]);

  const form = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await registerMutation.mutateAsync({
          fullName: value.fullName.trim(),
          email: value.email,
          password: value.password,
        });
        // Server returned 200 with alreadyRegistered = true → unverified account, OTP resent
        if (result?.alreadyRegistered) {
          navigate('/activate', {
            replace: true,
            state: {
              email: value.email,
              alreadyRegistered: true,
              otpId: result.otpId,
              expiresIn: result.expiresIn,
            },
          });
          return;
        }
        // Fresh registration — navigate to the OTP entry screen
        navigate('/activate', {
          replace: true,
          state: { email: value.email, otpId: result.otpId, expiresIn: result.expiresIn },
        });
      } catch (error: any) {
        const code = error?.code ?? error?.response?.data?.error?.code;
        if (code === 'CONFLICT') {
          toast.error('An account with this email already exists. Try signing in instead.');
          navigate('/login', { state: { message: 'Account already exists. Please sign in.' } });
          return;
        }
        // Other errors shown in the banner via registerMutation.error
      }
    },
  });

  return (
    <AuthShell>
      <div className="head">
        <h1>Create your account</h1>
        <p>Join Welynk in less than 60 seconds. Free forever for individuals.</p>
      </div>

      {registerMutation.error && (
        <div className="auth-error-banner" role="alert">
          {(registerMutation.error as any)?.response?.data?.error?.message ||
            'Registration failed. Please try again.'}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
      >
        {/* Full name */}
        <div style={{ marginBottom: 16 }}>
          <form.Field
            name="fullName"
            validators={{
              onBlur: ({ value }) => {
                const r = fullNameSchema.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
              onSubmit: ({ value }) => {
                const r = fullNameSchema.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
            }}
          >
            {(field) => (
              <FloatField
                id="reg-fullName"
                label="Full name"
                icon="user"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                autoComplete="name"
                error={field.state.meta.errors[0] as string | undefined}
              />
            )}
          </form.Field>
        </div>

        {/* Email */}
        <form.Field
          name="email"
          validators={{
            onBlur: ({ value }) => {
              const r = emailFieldSchema.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
            onSubmit: ({ value }) => {
              const r = emailFieldSchema.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <FloatField
              id="reg-email"
              label="Personal Email"
              type="email"
              icon="mail"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              autoComplete="email"
              error={field.state.meta.errors[0] as string | undefined}
            />
          )}
        </form.Field>

        {/* Password + strength meter */}
        <div style={{ marginTop: 16 }}>
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                const r = passwordRegisterSchema.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
              onSubmit: ({ value }) => {
                const r = passwordRegisterSchema.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
            }}
          >
            {(field) => (
              <>
                <FloatField
                  id="reg-password"
                  label="Password"
                  type="password"
                  icon="lock"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  autoComplete="new-password"
                  error={field.state.meta.errors[0] as string | undefined}
                />
                <PwStrengthMeter pw={field.state.value} />
              </>
            )}
          </form.Field>
        </div>

        {/* Confirm password */}
        <div style={{ marginTop: 16 }}>
          <form.Field
            name="confirmPassword"
            validators={{
              onBlur: ({ value, fieldApi }) => {
                const r = confirmPasswordSchema.safeParse(value);
                if (!r.success) return r.error.issues[0]?.message;
                const password = fieldApi.form.getFieldValue('password' as any);
                if (value !== password) return "Passwords don't match";
                return undefined;
              },
              onSubmit: ({ value, fieldApi }) => {
                const r = confirmPasswordSchema.safeParse(value);
                if (!r.success) return r.error.issues[0]?.message;
                const password = fieldApi.form.getFieldValue('password' as any);
                if (value !== password) return "Passwords don't match";
                return undefined;
              },
            }}
          >
            {(field) => (
              <FloatField
                id="reg-confirmPassword"
                label="Confirm password"
                type="password"
                icon="lock"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                autoComplete="new-password"
                error={field.state.meta.errors[0] as string | undefined}
              />
            )}
          </form.Field>
        </div>

        {/* Terms checkbox */}
        <label className="auth-checkbox" style={{ marginTop: 20, marginBottom: 20 }}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </span>
        </label>

        <button
          type="submit"
          disabled={registerMutation.isPending || !agreed}
          className="btn-auth-primary auth-cta"
        >
          {registerMutation.isPending ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <SsoButtons />

      <div className="auth-foot">
        Already on Welynk? <Link to="/login">Sign in</Link>
      </div>
    </AuthShell>
  );
};
